// src/Dashboard/pages/user/Inscription/InscriptionDashboard.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useInscriptionDashboard } from "./hooks/useInscriptionDashboard";

import InscriptionHeader from "./components/InscriptionHeader";
import InscriptionKpis from "./components/InscriptionKpis";
import InscriptionToolbar from "./components/InscriptionToolbar";
import AdherentDossierTable from "./components/AdherentDossierTable";
import UsefulDocumentsPanel from "./components/UsefulDocumentsPanel";
import DossierDrawer from "./components/DossierDrawer";
import DocumentsDrawer from "./components/DocumentsDrawer";
import RenewalDrawer from "./components/RenewalDrawer";
import TrialValidationDrawer from "./components/TrialValidationDrawer";

import { buildAttestationState } from "./utils/inscriptionUtils";

const CREATE_ADHERENT_ROUTE = "/ajouter-inscription-adherent";
const PAYMENT_ROUTE = "/dashboard/commandes-&-reglements";

export default function InscriptionDashboard() {
  useScrollToTop();

  const navigate = useNavigate();

  const {
    loading,
    error,

    user,
    adherents,
    filteredAdherents,
    dossiers,
    reglements,
    groupNames,
    segments,
    settings,
    activeSeason,

    summaries,
    stats,

    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    seasonFilter,
    setSeasonFilter,

    selected,
    drawer,
    openDossierDrawer,
    openDocumentsDrawer,
    openRenewalDrawer,
    openTrialDrawer,
    closeDrawer,

    refreshAll,
    refreshAdherent,
    refreshDossier,
  } = useInscriptionDashboard();

  const selectedDossier = selected ? dossiers[selected.id] || null : null;
  const selectedReglement = selected ? reglements[selected.id] || null : null;
  const selectedGroups = selected ? groupNames[selected.id] || [] : [];

  const handleCreateAdherent = () => {
    navigate(CREATE_ADHERENT_ROUTE);
  };

  const handleGoPayments = () => {
    navigate(PAYMENT_ROUTE);
  };

  const handleGoAttestation = (adherent, reglement) => {
    navigate("/attestation", {
      state: buildAttestationState({
        adherent,
        reglement,
        settings,
        activeSeason,
      }),
    });
  };

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1500px] space-y-5 px-3 sm:px-4 lg:px-6">
        <InscriptionHeader
          loading={loading}
          onRefresh={refreshAll}
          onCreateAdherent={handleCreateAdherent}
        />

        <InscriptionKpis stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="space-y-5 p-4 sm:p-5 lg:p-6">
            <InscriptionToolbar
              search={search}
              setSearch={setSearch}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              seasonFilter={seasonFilter}
              setSeasonFilter={setSeasonFilter}
              resultCount={filteredAdherents.length}
              totalCount={adherents.length}
              activeSeason={activeSeason}
            />

            <AdherentDossierTable
              loading={loading}
              error={error}
              adherents={filteredAdherents}
              summaries={summaries}
              dossiers={dossiers}
              reglements={reglements}
              groupNames={groupNames}
              activeSeason={activeSeason}
              onOpenDossier={openDossierDrawer}
              onOpenDocuments={openDocumentsDrawer}
              onOpenRenewal={openRenewalDrawer}
            />
          </div>
        </section>

        <UsefulDocumentsPanel segments={segments} />
      </div>

      <DossierDrawer
        isOpen={drawer === "dossier"}
        onClose={closeDrawer}
        adherent={selected}
        dossier={selectedDossier}
        reglement={selectedReglement}
        groups={selectedGroups}
        user={user}
        settings={settings}
        activeSeason={activeSeason}
        onOpenDocuments={() => selected && openDocumentsDrawer(selected)}
        onOpenRenewal={() => selected && openRenewalDrawer(selected)}
        onOpenTrial={() => selected && openTrialDrawer(selected)}
        onGoPayments={handleGoPayments}
        onGoAttestation={handleGoAttestation}
      />

      <DocumentsDrawer
        isOpen={drawer === "documents"}
        onClose={closeDrawer}
        adherent={selected}
        dossier={selectedDossier}
        refreshDossier={async () => {
          if (selected?.id) await refreshDossier(selected.id);
        }}
      />

      <RenewalDrawer
        isOpen={drawer === "renewal"}
        onClose={async (saved) => {
          closeDrawer();
          if (saved && selected?.id) {
            await refreshAdherent(selected.id);
          }
        }}
        adherent={selected}
        nextSeason={activeSeason}
        currentGroupNames={selectedGroups}
      />

      <TrialValidationDrawer
        isOpen={drawer === "trial"}
        onClose={async (updated) => {
          closeDrawer();
          if (updated && selected?.id) {
            await refreshAdherent(selected.id);
          }
        }}
        adherent={selected}
        user={user}
      />

      <ToastContainer />
    </section>
  );
}
